import { NOTHS_ID_TOKEN_STORAGE_KEY } from './constants';

/**
 * Whether a stored id-token is past its `exp`, or too damaged to tell.
 *
 * Mirrors @noths/noths-auth, which compares `exp` against the current second with no grace period.
 * A token we cannot decode is treated as expired: it is no more useful than one that has run out,
 * and sending it only earns an UNAUTHENTICATED from the API.
 */
const hasExpired = (idToken: string): boolean => {
  try {
    const [, payload] = idToken.split('.');
    const { exp } = JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };

    return typeof exp !== 'number' || Math.round(Date.now() / 1000) >= exp;
  } catch {
    return true;
  }
};

/**
 * The stored guest id-token, or null when there is not a usable one. An expired token is dropped
 * rather than returned, since nothing else refreshes this copy.
 *
 * This is read here rather than through NothsAuth.currentUser() on purpose. currentUser() *creates*
 * an anonymous user when it finds none, so using it to answer "is a token already available?" would
 * both mint anonymous identities on the PDP's basket calls, where today we send no x-id-token at
 * all, and hand back an anon token to ensureExpressGuest, which would then skip minting the guest
 * that carries the buyer's contact.
 *
 * It lives apart from guestAuth so that getBasketIdToken can read the token without importing the
 * module that calls getBasketIdToken, which would be a cycle.
 */
export const getStoredGuestIdToken = (): string | null => {
  const idToken = window.localStorage.getItem(NOTHS_ID_TOKEN_STORAGE_KEY);

  if (!idToken) {
    return null;
  }

  if (hasExpired(idToken)) {
    window.localStorage.removeItem(NOTHS_ID_TOKEN_STORAGE_KEY);

    return null;
  }

  return idToken;
};
